import {
  createContext,
  FunctionComponent,
  ReactNode,
  useEffect,
  useMemo,
  useState,
  useRef,
  Fragment,
} from 'react';
import {
  // eslint-disable-next-line @typescript-eslint/no-restricted-imports
  useUsers as useUsersAlloy,
  UsersContextValue,
  useClientSideDataModification,
  useCurrentToolId,
  TOOL_IDS,
  useDeletedUsers,
} from '@adsk/alloy-provider';
import { useContextWithValidation } from '@adsk/alloy-react-context';
import intersectionBy from 'lodash/intersectionBy';
import { AdminUser } from '../sdk';
import { useGetUsersMe } from '../hooks/queries';
import { setPendingUsersNameAsEmail } from '../helpers/userHelpers';

export const RFIsUserContext = createContext<UsersContextValue>({
  data: [],
  loading: true,
} as unknown as UsersContextValue);

const useShouldUseCustomProvider = () => {
  const currentToolId = useCurrentToolId();
  const isRFIsTool = currentToolId === TOOL_IDS.RFIS;
  return isRFIsTool;
};

export const useUsers = () => {
  const shouldUseCustomProvider = useShouldUseCustomProvider();
  if (!shouldUseCustomProvider) {
    // useUsersAlloy creates new object each time.
    const {
      data: alloyActiveUsers,
      loading: alloyActiveUsersLoading,
      error,
      retry,
      addUsers,
      updateUser,
      removeUserById,
    } = useUsersAlloy();

    const { data: deletedUsers, loading: deletedUsersLoading } = useDeletedUsers();
    return useMemo(
      () => ({
        data: [...alloyActiveUsers, ...deletedUsers],
        loading: alloyActiveUsersLoading || deletedUsersLoading,
        error,
        retry,
        addUsers,
        updateUser,
        removeUserById,
      }),
      [
        alloyActiveUsers,
        alloyActiveUsersLoading,
        deletedUsers,
        deletedUsersLoading,
        error,
        retry,
        addUsers,
        updateUser,
        removeUserById,
      ],
    );
  }

  return useContextWithValidation(RFIsUserContext, {
    hook: 'useUser',
    provider: 'RFIsUsersProvider',
  });
};

export const RFIsUsersProvider: FunctionComponent<ReactNode> = ({ children }) => {
  const shouldUseCustomProvider = useShouldUseCustomProvider();
  if (!shouldUseCustomProvider) {
    return <Fragment>{children}</Fragment>;
  }
  const [loading, setLoading] = useState(true);
  const alloyUsers = useUsersAlloy();
  const deletedUsers = useDeletedUsers();
  const usersMe = useGetUsersMe();
  const shouldAddExternalUsers = useRef(true);

  const {
    modifyData,
    addData: addUsers,
    updateEntity: updateUser,
    removeEntityById: removeUserById,
  } = useClientSideDataModification<AdminUser>(/** Session? */);

  const deletedUsersAutodeskId = useMemo(() => {
    const autodeskIds = new Set<string>();
    if (!deletedUsers.loading) {
      deletedUsers.data.forEach(({ autodeskId: id }) => autodeskIds.add(id));
    }

    return autodeskIds;
  }, [deletedUsers.data, deletedUsers.loading]);

  // Set loading to false when alloy, external users AND deleted user are fetched
  useEffect(() => {
    const isAllUsersDataFetched = !alloyUsers.loading && usersMe.isSuccess && !deletedUsers.loading;
    if (isAllUsersDataFetched) {
      setLoading(false);
    }
  }, [alloyUsers.loading, usersMe.isSuccess, deletedUsers.loading]);

  // Add alloy's SWR cached users to our context
  const data = useMemo(
    () => modifyData(alloyUsers.data as AdminUser[]),
    [modifyData, alloyUsers.data],
  );

  // Handle external users
  useEffect(() => {
    const isPossibleToAddExternalUsers = shouldAddExternalUsers.current && !loading && usersMe.data;
    if (isPossibleToAddExternalUsers) {
      // We filter out users who already exist in alloy users context so they won't get overridden
      const usersToFilter = intersectionBy(
        alloyUsers.data,
        usersMe.data.externalUsers,
        'autodeskId',
      ).map(({ autodeskId }) => autodeskId);
      const filteredExternalUsers = usersMe.data.externalUsers.filter(
        ({ autodeskId }) => !usersToFilter.includes(autodeskId),
      );
      const externalUsers = setPendingUsersNameAsEmail(filteredExternalUsers);
      const externalUsersExcludingDeleted = externalUsers.filter(
        ({ autodeskId: id }) => !deletedUsersAutodeskId.has(id),
      );
      addUsers(externalUsersExcludingDeleted);
      shouldAddExternalUsers.current = false;
    }
  }, [usersMe.data, alloyUsers.data, addUsers, loading, deletedUsersAutodeskId, deletedUsers.data]);

  useEffect(() => {
    if (!shouldAddExternalUsers.current) {
      addUsers(deletedUsers.data as AdminUser[]);
    }
  }, [shouldAddExternalUsers.current]);

  const { retry, error } = alloyUsers;
  const value = useMemo(
    () => ({
      data,
      loading,
      error,
      retry,
      addUsers,
      updateUser,
      removeUserById,
    }),
    [addUsers, updateUser, data, error, loading, removeUserById, retry],
  );

  return <RFIsUserContext.Provider value={value}>{children}</RFIsUserContext.Provider>;
};

export default RFIsUsersProvider;
